import { createMutation, createQuery } from 'react-query-kit';

import { createData, fetchData } from 'services/http/http';
import { TaskDTO } from 'support/types';
import portalSchema from 'support/types/schema-portal';

export const isActionableTask = (task: TaskDTO): boolean => task.state !== 'COMPLETED' && task.state !== 'IN_REVIEW';

type GetRelationshipTasksResponse =
  portalSchema.paths['/v1/relationships/{relationshipId}/tasks']['get']['responses']['200']['content']['application/json'];

export const useRelationshipTasks = createQuery<
  GetRelationshipTasksResponse,
  { relationshipId: string | undefined },
  Error
>({
  primaryKey: '/relationships/:relationshipId/tasks',
  queryFn: async ({ queryKey: [, { relationshipId }] }) => {
    const { data } = await fetchData<GetRelationshipTasksResponse>(`/v1/relationships/${relationshipId}/tasks`);
    return data;
  },
  enabled: (_, { relationshipId }) => !!relationshipId,
});

type GetTaskFileUploadUrlResponse =
  portalSchema.paths['/v1/relationships/{relationshipId}/tasks/{taskId}/file-upload-url']['post']['responses']['200']['content']['application/json'];

export type GetTaskFileUploadUrlRequest =
  portalSchema.paths['/v1/relationships/{relationshipId}/tasks/{taskId}/file-upload-url']['post']['requestBody']['content']['application/json'];

export const useGetTaskFileUploadUrl = createMutation<
  GetTaskFileUploadUrlResponse['data'],
  { relationshipId: string; taskId: string; payload: GetTaskFileUploadUrlRequest },
  Error
>({
  mutationFn: async ({ relationshipId, taskId, payload }) => {
    const { data } = await createData<GetTaskFileUploadUrlResponse>(
      `/v1/relationships/${relationshipId}/tasks/${taskId}/file-upload-url`,
      payload,
    );
    return data?.data;
  },
});

type GetTaskFileDownloadUrlResponse =
  portalSchema.paths['/v1/relationships/{relationshipId}/tasks/{taskId}/files/{name}']['get']['responses']['200']['content']['application/json'];

export const useGetTaskFileDownloadUrl = createMutation<
  GetTaskFileDownloadUrlResponse['data'],
  { relationshipId: string; taskId: string; name: string },
  Error
>({
  mutationFn: async ({ relationshipId, taskId, name }) => {
    const { data } = await fetchData<GetTaskFileDownloadUrlResponse>(
      `/v1/relationships/${relationshipId}/tasks/${taskId}/files/${name}`,
    );
    return data?.data;
  },
});

type SubmitTaskResponse =
  portalSchema.paths['/v1/relationships/{relationshipId}/tasks/{taskId}']['post']['responses']['200']['content']['application/json'];

type SubmitTaskRequest =
  portalSchema.paths['/v1/relationships/{relationshipId}/tasks/{taskId}']['post']['requestBody']['content']['application/json'];

export const useSubmitTask = createMutation<
  SubmitTaskResponse,
  { relationshipId: string; taskId: string; payload: SubmitTaskRequest },
  Error
>({
  mutationFn: async ({ relationshipId, taskId, payload }) => {
    const { data } = await createData<SubmitTaskResponse>(
      `/v1/relationships/${relationshipId}/tasks/${taskId}`,
      payload,
    );
    return data;
  },
});
